import { bookScopingCall, downloadBlueprintPdf, generateBlueprint } from "../api/client";
import type { BlueprintDocument, RankedUseCase, ReadinessDimension, ScopingCallContact } from "../api/types";
import type { Stage } from "./Stage0Onramp";

const DOWNLOAD_ICON = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>`;
const ARROW_ICON = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>`;

export class Stage4Blueprint implements Stage {
  private sessionId: string;
  private onStartOver: () => void;

  constructor(sessionId: string, onStartOver: () => void = () => {}) {
    this.sessionId = sessionId;
    this.onStartOver = onStartOver;
  }

  mount(container: HTMLElement): void {
    container.innerHTML = `
      <div class="blueprint-doc" id="bp-loading" style="padding:var(--space-2xl);text-align:center;border-color:transparent;">
        <div style="display:inline-flex;flex-direction:column;align-items:center;gap:var(--space-md);">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--swo-blue)" stroke-width="1.5" stroke-linecap="round" aria-hidden="true" style="animation:spin 1.2s linear infinite"><circle cx="12" cy="12" r="10" stroke-dasharray="40" stroke-dashoffset="10"/></svg>
          <p style="color:var(--swo-ash);font-size:14px;margin:0;">Generating your AI opportunity blueprint...</p>
        </div>
      </div>
    `;
    this._load(container);
  }

  unmount(): void {}

  private async _load(container: HTMLElement): Promise<void> {
    try {
      const bp = await generateBlueprint(this.sessionId);
      container.innerHTML = this._renderBlueprint(bp);
      container
        .querySelector<HTMLButtonElement>(".btn-download-pdf")
        ?.addEventListener("click", () => downloadBlueprintPdf(this.sessionId));
      container
        .querySelector<HTMLButtonElement>(".btn-new-assessment")
        ?.addEventListener("click", () => this.onStartOver());
      container
        .querySelector<HTMLButtonElement>(".btn-book-call")
        ?.addEventListener("click", () => this._openDialog());
    } catch {
      container.innerHTML = `
        <div class="blueprint-doc" style="padding:var(--space-2xl);">
          <p style="color:var(--swo-red);margin:0 0 var(--space-md);">Blueprint generation failed. Please retry.</p>
          <button class="btn primary" id="retry-btn" type="button">Retry</button>
        </div>
      `;
      container.querySelector<HTMLButtonElement>("#retry-btn")?.addEventListener("click", () => this._load(container));
    }
  }

  private _renderBlueprint(bp: BlueprintDocument): string {
    return `
      <div class="blueprint-doc">

        <div class="bp-hero">
          <div class="bp-eyebrow">AI Opportunity Blueprint</div>
          <h1 class="bp-title">${this._esc(bp.org_name)}</h1>
          <p class="bp-subtitle">Generated ${new Date(bp.generated_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })} &middot; ${bp.session_duration_minutes} minutes</p>
          <div class="bp-actions">
            <button class="btn btn-download-pdf" type="button">
              ${DOWNLOAD_ICON} Download PDF
            </button>
          </div>
        </div>

        <div class="bp-section">
          <h2>Executive summary</h2>
          <div class="bp-summary">
            ${bp.executive_summary.split("\n").map((p) => p.trim() ? `<p>${this._esc(p)}</p>` : "").join("")}
          </div>
        </div>

        <div class="bp-section">
          <h2>Recommended use cases — ranked</h2>
          ${bp.use_cases.map((uc) => this._renderUseCase(uc)).join("")}
        </div>

        <div class="bp-section">
          <h2>Data &amp; readiness check</h2>
          <div class="readiness-grid">
            ${bp.readiness.map((r) => this._renderReadiness(r)).join("")}
          </div>
        </div>

        <div class="bp-section">
          <h2>Your next 30 days</h2>
          <div class="next-steps">
            ${bp.next_30_days.map((s) => `
              <div class="step-item">
                <div class="step-num" aria-hidden="true"></div>
                <div class="step-content">
                  <h4>${this._esc(s.title)}</h4>
                  <p>${this._esc(s.description)}</p>
                </div>
              </div>
            `).join("")}
          </div>
        </div>

        <div class="bp-section">
          <div class="cta-card">
            <div>
              <h3>Ready to take this further.</h3>
              <p>${this._esc(bp.cta_text)}</p>
            </div>
            ${this._isBooked()
              ? this._bookedPlaceholderHTML()
              : `<button class="btn primary btn-book-call" type="button">
              Book a scoping call ${ARROW_ICON}
            </button>`}
          </div>
        </div>

        <div style="text-align:center;padding:var(--space-xl) 0 var(--space-2xl);">
          <button class="btn-ghost btn-new-assessment" type="button">
            Start a new assessment
          </button>
        </div>

      </div>
    `;
  }

  private _renderUseCase(uc: RankedUseCase): string {
    const impactClass = uc.business_impact === "High" ? "good" : "med";
    const effortClass = uc.implementation_effort === "Low" ? "good" : "med";
    return `
      <div class="uc-detail${uc.is_parked ? " uc-parked" : ""}">
        <div class="uc-detail-head">
          <div style="display:flex;gap:var(--space-md);align-items:flex-start;">
            <div class="uc-rank" aria-hidden="true">${uc.rank}</div>
            <div>
              <h3 class="uc-detail-title">
                ${this._esc(uc.name)}${uc.is_parked ? `<span style="font-size:11px;color:var(--swo-ash);font-weight:normal;margin-left:var(--space-sm);">(parked)</span>` : ""}
              </h3>
              <div class="uc-detail-tag">${this._esc(uc.type_tag)}</div>
            </div>
          </div>
        </div>
        <p class="uc-why">${this._esc(uc.description)}</p>
        ${uc.is_parked && uc.park_rationale ? `<p style="font-size:12px;color:var(--swo-granite);margin-top:var(--space-xs);">${this._esc(uc.park_rationale)}</p>` : ""}
        <div class="uc-scores">
          <div class="score">
            <div class="score-label">Business impact</div>
            <div class="score-value ${impactClass}">${this._esc(uc.business_impact)}</div>
            <div class="score-rationale">${this._esc(uc.business_impact_rationale)}</div>
          </div>
          <div class="score">
            <div class="score-label">Implementation effort</div>
            <div class="score-value ${effortClass}">${this._esc(uc.implementation_effort)}</div>
            <div class="score-rationale">${this._esc(uc.implementation_effort_rationale)}</div>
          </div>
          <div class="score">
            <div class="score-label">Time to first value</div>
            <div class="score-value good">${this._esc(uc.time_to_value)}</div>
            <div class="score-rationale">${this._esc(uc.time_to_value_rationale)}</div>
          </div>
          <div class="score">
            <div class="score-label">Rough cost band</div>
            <div class="score-value">${this._esc(uc.cost_band_eur)}</div>
            <div class="score-rationale">${this._esc(uc.cost_band_rationale)}</div>
          </div>
        </div>
      </div>
    `;
  }

  private _renderReadiness(r: ReadinessDimension): string {
    const statusClass = r.status === "good" ? "good" : r.status === "partial" ? "med" : "bad";
    const statusLabel = r.status === "good" ? "Ready" : r.status === "partial" ? "Partial" : "Needs work";
    const label = r.dimension.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    return `
      <div class="readiness-item">
        <div class="readiness-label">${label}</div>
        <div class="readiness-value ${statusClass}">${statusLabel} — ${this._esc(r.note)}</div>
      </div>
    `;
  }

  private _field(
    name: string,
    label: string,
    placeholder: string,
    required: boolean,
    type = "text",
    fullWidth = false,
  ): string {
    const optLabel = !required
      ? `<span style="color:var(--text-muted);font-weight:400;text-transform:none;letter-spacing:0;"> (optional)</span>`
      : "";
    const wrapStyle = fullWidth ? `style="width:100%;"` : "";
    return `
      <div ${wrapStyle}>
        <label style="display:block;font-size:11px;font-weight:600;color:var(--text-body);
                       text-transform:uppercase;letter-spacing:0.04em;margin-bottom:5px;">
          ${label}${optLabel}
        </label>
        <input name="${name}" type="${type}" placeholder="${placeholder}" ${required ? "required" : ""}
          style="width:100%;box-sizing:border-box;background:var(--surface-input);
                 border:var(--border-subtle);border-radius:var(--radius-md);
                 padding:9px 11px;font-size:13px;color:var(--text-primary);" />
      </div>`;
  }

  private _openDialog(): void {
    document.querySelector(".scoping-dialog-overlay")?.remove();

    const overlay = document.createElement("div");
    overlay.className = "scoping-dialog-overlay";
    overlay.style.cssText =
      "position:fixed;inset:0;background:rgba(0,0,0,0.6);display:flex;" +
      "align-items:center;justify-content:center;z-index:1000;padding:var(--space-md);";

    overlay.innerHTML = `
      <div style="background:var(--surface-page,#141416);border:var(--border-medium);
                  border-radius:var(--radius-lg);padding:28px;width:100%;max-width:460px;
                  box-shadow:0 24px 60px rgba(0,0,0,0.7);">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:16px;">
          <div>
            <h3 style="margin:0 0 4px;font-size:17px;color:var(--text-primary);">Book a scoping call</h3>
            <p style="margin:0;font-size:13px;color:var(--text-muted);">
              Leave your details and we'll reach out within 1–2 business days.
            </p>
          </div>
          <button class="dialog-close" type="button"
            style="background:none;border:none;color:var(--text-muted);font-size:22px;cursor:pointer;
                   padding:0 0 0 12px;line-height:1;">&times;</button>
        </div>

        <form class="scoping-form" novalidate>
          <div style="display:flex;flex-direction:column;gap:12px;">
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;">
              ${this._field("name", "Name", "Jane Smith", true)}
              ${this._field("email", "Work email", "jane@acme.com", true, "email")}
            </div>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;">
              ${this._field("company", "Company", "Acme Corp", true)}
              ${this._field("role", "Role / title", "Head of IT", true)}
            </div>
            ${this._field("phone", "Phone", "+41 ...", false, "tel", true)}
            <div>
              <label style="display:block;font-size:11px;font-weight:600;color:var(--text-body);
                             text-transform:uppercase;letter-spacing:0.04em;margin-bottom:5px;">
                Message
                <span style="color:var(--text-muted);font-weight:400;text-transform:none;
                              letter-spacing:0;"> (optional)</span>
              </label>
              <textarea name="message" placeholder="Anything you'd like us to know before the call..."
                rows="3"
                style="width:100%;box-sizing:border-box;background:var(--surface-input);
                       border:var(--border-subtle);border-radius:var(--radius-md);
                       padding:9px 11px;font-size:13px;color:var(--text-primary);
                       font-family:var(--font);resize:vertical;"></textarea>
            </div>

            <div class="form-error" style="display:none;color:var(--swo-red);font-size:13px;"></div>

            <div style="display:flex;gap:10px;margin-top:4px;justify-content:flex-end;">
              <button class="dialog-cancel btn" type="button" style="padding:10px 16px;">Cancel</button>
              <button class="btn primary submit-btn" type="submit" style="padding:10px 20px;">Send request</button>
            </div>
          </div>
        </form>
      </div>`;

    overlay.addEventListener("click", (e) => { if (e.target === overlay) overlay.remove(); });
    overlay.querySelector(".dialog-close")?.addEventListener("click", () => overlay.remove());
    overlay.querySelector(".dialog-cancel")?.addEventListener("click", () => overlay.remove());
    overlay.querySelector("form.scoping-form")?.addEventListener("submit", async (e) => {
      e.preventDefault();
      await this._handleSubmit(overlay);
    });

    document.body.appendChild(overlay);
    (overlay.querySelector("input[name=name]") as HTMLInputElement | null)?.focus();
  }

  private async _handleSubmit(overlay: HTMLElement): Promise<void> {
    const form = overlay.querySelector("form.scoping-form") as HTMLFormElement;
    const errorEl = overlay.querySelector(".form-error") as HTMLElement;
    const submitBtn = overlay.querySelector<HTMLButtonElement>(".submit-btn")!;

    const val = (name: string) =>
      (form.querySelector<HTMLInputElement | HTMLTextAreaElement>(`[name=${name}]`)!).value.trim();

    const name = val("name");
    const email = val("email");
    const company = val("company");
    const role = val("role");
    const phone = val("phone") || undefined;
    const message = val("message") || undefined;

    if (!name || !email || !company || !role) {
      errorEl.textContent = "Please fill in all required fields.";
      errorEl.style.display = "block";
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errorEl.textContent = "Please enter a valid work email address.";
      errorEl.style.display = "block";
      return;
    }

    errorEl.style.display = "none";
    submitBtn.disabled = true;
    submitBtn.textContent = "Sending…";

    const contact: ScopingCallContact = { name, email, company, role, phone, message };

    try {
      await bookScopingCall(this.sessionId, contact);
      this._markBooked();
      const bookBtn = document.querySelector<HTMLElement>(".btn-book-call");
      if (bookBtn) {
        const tmp = document.createElement("div");
        tmp.innerHTML = this._bookedPlaceholderHTML();
        bookBtn.replaceWith(tmp.firstElementChild!);
      }
      overlay.remove();
      this._showToast("Request sent — we'll be in touch!");
    } catch {
      submitBtn.disabled = false;
      submitBtn.textContent = "Send request";
      errorEl.innerHTML =
        "Something went wrong. " +
        '<button class="btn-ghost retry-btn" type="button" ' +
        'style="color:var(--swo-blue);text-decoration:underline;background:none;' +
        'border:none;cursor:pointer;font-size:13px;padding:0;">Retry</button>';
      errorEl.style.display = "block";
      errorEl.querySelector(".retry-btn")?.addEventListener("click", () =>
        this._handleSubmit(overlay)
      );
    }
  }

  private _showToast(message: string): void {
    const toast = document.createElement("div");
    toast.style.cssText =
      "position:fixed;bottom:var(--space-lg);left:50%;transform:translateX(-50%);" +
      "background:var(--swo-teal);color:#fff;border-radius:var(--radius-md);" +
      "padding:10px 20px;font-size:13px;font-weight:500;z-index:1100;" +
      "display:flex;align-items:center;gap:8px;" +
      "box-shadow:0 4px 16px rgba(0,0,0,0.4);white-space:nowrap;";
    toast.innerHTML =
      `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" ` +
      `stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">` +
      `<polyline points="20 6 9 17 4 12"/></svg>` +
      this._esc(message);
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 4000);
  }

  private _isBooked(): boolean {
    try {
      return localStorage.getItem(`scopingCallBooked:${this.sessionId}`) === "1";
    } catch {
      return false;
    }
  }

  private _markBooked(): void {
    try {
      localStorage.setItem(`scopingCallBooked:${this.sessionId}`, "1");
    } catch {
      // localStorage unavailable (e.g. private-mode Safari) — degrade gracefully
    }
  }

  private _bookedPlaceholderHTML(): string {
    return (
      `<div class="booked-placeholder" style="display:flex;align-items:center;gap:12px;` +
      `background:var(--swo-teal);color:#fff;border-radius:var(--radius-md);` +
      `padding:12px 20px;font-size:13px;font-weight:500;">` +
      `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" ` +
      `stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">` +
      `<polyline points="20 6 9 17 4 12"/></svg>` +
      `<div>` +
      `<div style="font-weight:600;">We look forward to connecting with you!</div>` +
      `<div style="font-size:11px;opacity:0.85;margin-top:2px;">Our team will be in touch shortly.</div>` +
      `</div></div>`
    );
  }

  private _esc(text: string): string {
    return text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }
}
